import type { FC, PropsWithChildren } from 'react';
import { createContext, useEffect, useState } from 'react';
import type { Attachment } from '@ContractBuilder/types';
import type { ResourceSection } from '@root/@types/types';

import { isEndorsementDetailsNavItem, type NavItem } from '../types';

interface NavigationDragContextValue {
  navItems: NavItem[];
  sections: ResourceSection[];
  attachments?: Attachment[];
  activeSectionId?: string;
  getShouldDisableOperations: (section: NavItem) => boolean;
  isDragDisabled: boolean;
  activeId: string | null;
  activeType: 'section' | 'block' | null;
  isLoading: boolean;
  setActiveId: (id: string | null) => void;
  setActiveType: (type: 'section' | 'block' | null) => void;
  setIsLoading: (loading: boolean) => void;
  setNavItems: (items: NavItem[]) => void;
  setSections: (sections: ResourceSection[]) => void;
}

export const NavigationDragContext = createContext<NavigationDragContextValue | null>(null);

type NavigationDragProviderProps = PropsWithChildren<{
  navItems: NavItem[];
  attachments?: Attachment[];
  activeSectionId?: string;
  getShouldDisableOperations?: (section: NavItem) => boolean;
  isDragDisabled: boolean;
}>;

const toResourceSections = (items: NavItem[]): ResourceSection[] =>
  items.filter((s): s is ResourceSection => !isEndorsementDetailsNavItem(s));

export const NavigationDragProvider: FC<NavigationDragProviderProps> = ({
  navItems: propNavItems,
  attachments: propAttachments,
  activeSectionId,
  getShouldDisableOperations = () => false,
  isDragDisabled,
  children,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<'section' | 'block' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [navItems, setNavItems] = useState<NavItem[]>(propNavItems);
  const [attachments, setAttachments] = useState<Attachment[]>(propAttachments ?? []);

  useEffect(() => {
    setNavItems(propNavItems);
  }, [propNavItems]);

  useEffect(() => {
    if (propAttachments) {
      setAttachments(propAttachments);
    }
  }, [propAttachments]);

  const sections = toResourceSections(navItems);

  const setSections = (updated: ResourceSection[]) => {
    setNavItems((prev) => {
      const iterator = updated[Symbol.iterator]();
      return prev.map((item) => (isEndorsementDetailsNavItem(item) ? item : (iterator.next().value ?? item)));
    });
  };

  const value: NavigationDragContextValue = {
    navItems,
    sections,
    attachments,
    activeSectionId,
    getShouldDisableOperations,
    isDragDisabled,
    activeId,
    activeType,
    isLoading,
    setActiveId,
    setActiveType,
    setIsLoading,
    setNavItems,
    setSections,
  };

  return <NavigationDragContext.Provider value={value}>{children}</NavigationDragContext.Provider>;
};
